/**
 * Blinkit-theme order page (``/orders/:id``). Data, polling, cancel, review and return flows come from
 * useOrderTrackController, and status / return-window display rules from deriveOrderView — the classic page's own
 * logic. The map is the shared OrderTrackMap. The bill uses the same "Price details" as checkout, read from the
 * order's stored totals.
 */
import { Link, useLocation } from "react-router-dom";
import {
    Bike, CheckCircle2, ChevronRight, Headphones, Loader2, MapPin, MessageCircle, Package, Phone, RefreshCw, Star, Store,
    Undo2, X, XCircle,
} from "lucide-react";
import { formatDeliveryEta } from "../../../lib/deliveryEta";
import { fmtInr, orderBillTotals } from "../../../lib/priceBreakdown";
import CallButton from "../../../components/CallButton";
import ReviewModal from "../../../components/ReviewModal";
import { BkCard, BkEmptyState, BkErrorState, BkPageHeader } from "../../../components/blinkit/BkUI";
import OrderTrackMap from "../_partials/OrderTrackMap";
import { deriveOrderView, useOrderTrackController } from "../controllers/useOrderTrackController";
import LseOrderExtras from "../../../components/lse/LseOrderExtras";
import { OrderGroupChip } from "../../../components/MultiShop";
import { BkPriceDetails } from "./BkCheckoutParts";

const STEPS = ["Placed", "Confirmed", "Preparing", "On the way", "Delivered"];
const STEP_OF = {
    PLACED: 0, LOOKING_FOR_RIDER: 0, RIDER_ASSIGNED: 0, CONFIRMED: 1, PREPARING: 2, READY: 2,
    PICKED_UP: 3, OUT_FOR_DELIVERY: 3, DELIVERED: 4,
};

function Progress({ status }) {
    const at = STEP_OF[status];
    if (at == null) return null;
    return (
        <ol className="mt-4 grid grid-cols-5 gap-1.5" aria-label="Order progress" data-testid="bk-order-progress">
            {STEPS.map((label, i) => (
                <li key={label} className="min-w-0" aria-current={i === at ? "step" : undefined}>
                    <span className={`block h-1.5 rounded-full ${i <= at ? "bg-primary" : "bg-muted"}`} />
                    <span className={`mt-1.5 block text-[10.5px] leading-tight truncate ${i === at ? "font-extrabold text-primary" : i < at ? "font-semibold text-foreground/70" : "text-muted-foreground"}`}>{label}</span>
                </li>
            ))}
        </ol>
    );
}

function PersonRow({ icon: Icon, title, name, sub, phone, testid }) {
    return (
        <div className="flex items-center gap-3" data-testid={testid}>
            <span className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon size={18} /></span>
            <span className="min-w-0 flex-1">
                <span className="block text-[11.5px] font-semibold text-muted-foreground">{title}</span>
                <span className="block text-[14px] font-bold truncate">{name || "—"}</span>
                {sub && <span className="block text-[12px] text-muted-foreground truncate">{sub}</span>}
            </span>
            {phone && (
                <CallButton phone={phone} testid={`${testid}-call`}
                            className="shrink-0 h-9 px-3 rounded-lg bg-primary text-primary-foreground text-[12.5px] font-bold inline-flex items-center gap-1.5"><Phone size={14} /> Call</CallButton>
            )}
        </div>
    );
}

const fmtTime = (ts) => { try { return new Date(ts).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const PAY_LABEL = { COD: "Cash on delivery", RAZORPAY: "Paid online", ONLINE: "Paid online", WALLET: "Wallet" };

export default function BkOrderDetailView() {
    const { state } = useLocation();
    const c = useOrderTrackController();
    const {
        id, tenant, data, loadError, track, refreshing, showReview, setShowReview, existingReview, cancelling, returnRequest,
        showReturnModal, setShowReturnModal, returnReason, setReturnReason, submittingReturn, loadOrder, loadTrack, loadReviewState,
        submitReturnRequest, cancelOrder, points,
    } = c;

    if (!data) {
        return (
            <div className="pb-24" data-testid="bk-order-page">
                <BkPageHeader title="Order" back="/orders" />
                {loadError === "missing" ? (
                    <BkEmptyState icon={Package} title="Order not found" body="It may belong to another account." testid="bk-order-missing"
                                  action={<Link to="/orders" className="inline-flex h-10 items-center px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold">See my orders</Link>} />
                ) : loadError ? (
                    <BkErrorState onRetry={loadOrder} testid="bk-order-error" />
                ) : (
                    <div className="px-4 pt-4 space-y-3" aria-hidden="true" data-testid="bk-order-loading">
                        {[140, 220, 180].map((h, i) => <div key={i} className="rounded-2xl bg-card border border-border/80 animate-pulse" style={{ height: h }} />)}
                    </div>
                )}
            </div>
        );
    }

    const {
        o, events, status, returnWindowDays, returnWindowOpen, reviewBlockedByReturn, stepLabel, stepColor, riderStatus, hasMap,
    } = deriveOrderView({ data, track, tenant, returnRequest, points });
    const cancelled = status === "CANCELLED" || status === "REJECTED";
    // A Location Service Engine next-day / scheduled order shows its date + slot (order extras) instead of an ETA.
    const planned = ["NEXT_DAY", "SCHEDULED"].includes(o.delivery_plan?.mode);
    const etaText = !cancelled && status !== "DELIVERED" && o.eta_minutes && !planned ? formatDeliveryEta(tenant, o.eta_minutes) : null;
    const justPlaced = !!state?.justPlaced && !cancelled;
    const { totals: billTotals, walletApplied } = orderBillTotals(o.totals);
    const paid = o.payment_status === "PAID";
    const toPayLabel = paid ? "Paid" : o.payment_method === "COD" ? "Pay on delivery" : "Amount due";

    return (
        <div className="pb-24 min-w-0" data-testid="bk-order-page">
            <BkPageHeader
                title={<span data-testid="order-id">{o.order_unique_id}</span>}
                subtitle={`Order from ${o.restaurant_name}`}
                back="/orders"
                right={<>
                    <button type="button" onClick={loadTrack} disabled={refreshing} aria-label="Refresh status" data-testid="track-refresh"
                            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted disabled:opacity-50">
                        <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
                    </button>
                    <Link to="/support" aria-label="Help" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted"><Headphones size={19} /></Link>
                </>}
            />

            {justPlaced && (
                <div className="mx-4 mt-4 rounded-2xl bg-emerald-600 text-white p-4 flex items-center gap-3 fade-up" data-testid="bk-order-placed" role="status">
                    <CheckCircle2 size={34} className="shrink-0" />
                    <div className="min-w-0">
                        <p className="text-[17px] font-extrabold leading-tight">Order placed!</p>
                        <p className="text-[12.5px] opacity-90">{o.restaurant_name} has your order. We&apos;ll keep this page updated.</p>
                    </div>
                </div>
            )}

            {/* Only on an order that came from a multi-shop checkout: the way back to its sibling orders. */}
            {o.order_group_id && <div className="mx-4 mt-3"><OrderGroupChip order={o} link /></div>}

            <div className="px-4 pt-4 lg:grid lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:gap-5 lg:items-start">
                <div className="space-y-3 min-w-0">
                    {/* Status */}
                    <section className={`rounded-2xl border p-4 ${cancelled ? "bg-destructive/[0.06] border-destructive/25" : "bg-card border-border/80"}`} data-testid="track-status-card">
                        <div className="flex items-start gap-3">
                            {cancelled ? <XCircle size={28} className="text-destructive shrink-0" />
                                : status === "DELIVERED" ? <CheckCircle2 size={28} className="text-emerald-600 shrink-0" />
                                    : <span className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center" style={{ background: `${stepColor}22` }}><span className="w-3 h-3 rounded-full animate-pulse" style={{ background: stepColor }} /></span>}
                            <div className="min-w-0 flex-1">
                                <h2 className="text-[20px] font-extrabold leading-tight" data-testid="order-status">{stepLabel}</h2>
                                <p className="text-[12.5px] text-muted-foreground mt-0.5">
                                    {cancelled ? "This order was cancelled. Anything you paid for it is refunded to your wallet."
                                        : status === "DELIVERED" ? `Delivered${o.delivered_at ? ` · ${fmtTime(o.delivered_at)}` : ""}`
                                            : etaText ? <span data-testid="order-eta">Arriving in about {etaText}</span> : "We'll update this as the store and rider move it along."}
                                </p>
                                {riderStatus && (
                                    <span data-testid="rider-status" className="mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: riderStatus.color }}>
                                        <Bike size={12} /> {riderStatus.label}
                                    </span>
                                )}
                            </div>
                        </div>
                        {!cancelled && <Progress status={status} />}
                    </section>

                    {/* Actions */}
                    {status === "PLACED" && (
                        <div>
                            <button type="button" data-testid="cancel-order-btn" onClick={cancelOrder} disabled={cancelling}
                                    className="w-full h-11 rounded-xl border border-destructive/40 text-destructive text-[13.5px] font-bold inline-flex items-center justify-center gap-2 hover:bg-destructive/5 disabled:opacity-50">
                                {cancelling ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />} Cancel order
                            </button>
                            <p className="text-[11.5px] text-muted-foreground text-center mt-1.5">You can cancel until the store starts preparing. Refunds go to your wallet.</p>
                        </div>
                    )}
                    {status === "DELIVERED" && !reviewBlockedByReturn && (
                        <button type="button" data-testid="open-review" onClick={() => setShowReview(true)}
                                className="w-full flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-left text-amber-900">
                            <Star size={20} className="fill-amber-500 text-amber-500 shrink-0" />
                            <span className="min-w-0 flex-1">
                                <span className="block text-[14px] font-bold">{existingReview ? `You rated this ${existingReview.overall_rating}/5` : "Rate this order"}</span>
                                <span className="block text-[12px] opacity-80">{existingReview ? "Tap to edit your review" : "Takes 10 seconds"}</span>
                            </span>
                            <ChevronRight size={17} className="shrink-0" />
                        </button>
                    )}
                    {status === "DELIVERED" && tenant?.returns_enabled && (
                        <div data-testid="return-section">
                            {returnRequest ? (
                                <div data-testid="return-status-pill"
                                     className={`rounded-2xl border px-4 py-3 ${returnRequest.status === "APPROVED" ? "bg-emerald-50 border-emerald-300" : returnRequest.status === "REJECTED" ? "bg-destructive/[0.06] border-destructive/30" : "bg-amber-50 border-amber-300"}`}>
                                    <p className="text-[13.5px] font-bold">
                                        {returnRequest.status === "APPROVED" && `Return approved — ₹${returnRequest.refund_amount} refunded to your wallet`}
                                        {returnRequest.status === "REJECTED" && "Return not approved"}
                                        {returnRequest.status === "PENDING" && "Return requested — awaiting review"}
                                    </p>
                                    {returnRequest.status === "REJECTED" && returnRequest.reject_reason && <p className="text-[12px] text-muted-foreground mt-1">{returnRequest.reject_reason}</p>}
                                </div>
                            ) : returnWindowOpen ? (
                                <button type="button" data-testid="request-return-btn" onClick={() => setShowReturnModal(true)}
                                        className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-left">
                                    <Undo2 size={19} className="text-muted-foreground shrink-0" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-[14px] font-bold">Request a return</span>
                                        <span className="block text-[12px] text-muted-foreground">Item damaged, wrong or missing?</span>
                                    </span>
                                    <ChevronRight size={17} className="text-muted-foreground shrink-0" />
                                </button>
                            ) : (
                                <p className="rounded-2xl border border-border bg-muted/50 px-4 py-3 text-[12px] text-muted-foreground" data-testid="return-window-closed">
                                    The return window for this order ({returnWindowDays} day{returnWindowDays !== 1 ? "s" : ""} from delivery) has closed.
                                </p>
                            )}
                        </div>
                    )}

                    {(track?.rider || track?.merchant_contact) && (
                        <BkCard testid="bk-order-people">
                            <div className="space-y-3">
                                {track?.rider && (
                                    <PersonRow icon={Bike} title="Your delivery partner" name={track.rider.name} testid="track-rider-card"
                                               sub={[track.rider.vehicle_type, track.rider.vehicle_number].filter(Boolean).join(" · ") || null} phone={track.rider.mobile_number} />
                                )}
                                {track?.merchant_contact && (
                                    <PersonRow icon={Store} title="Store" name={track.shop?.name} testid="track-merchant-card" phone={track.merchant_contact.mobile_number} />
                                )}
                            </div>
                        </BkCard>
                    )}

                    {/* Location Service Engine tenants only (renders nothing otherwise). */}
                    <LseOrderExtras orderId={o.id} status={o.status} riderStatus={o.rider_status} onLiveTick={loadTrack} />

                    <BkCard title={`${(o.items || []).reduce((n, i) => n + (i.quantity || 0), 0)} items in this order`} icon={Package} testid="track-summary">
                        <ul className="divide-y divide-border">
                            {(o.items || []).map((it) => (
                                <li key={(it.product_id || "") + (it.variant_id || "") + it.name} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                                    <span className="w-11 h-11 shrink-0 rounded-lg bg-muted/60 overflow-hidden flex items-center justify-center">
                                        {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-contain" /> : <Package size={16} className="text-muted-foreground/60" />}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-[13px] font-semibold leading-snug line-clamp-2">{it.name}</span>
                                        <span className="block text-[12px] text-muted-foreground">{fmtInr(it.selling_price)} × {it.quantity}</span>
                                    </span>
                                    <span className="shrink-0 text-[13px] font-bold tabular-nums">{fmtInr(it.line_total ?? ((it.selling_price || 0) * (it.quantity || 0)))}</span>
                                </li>
                            ))}
                        </ul>
                    </BkCard>
                </div>

                <div className="space-y-3 mt-3 lg:mt-0 min-w-0 lg:sticky lg:top-20">
                    <BkPriceDetails
                        totals={billTotals} walletApplied={walletApplied} title="Bill details" testid="bk-order-bill" toPayLabel={toPayLabel}
                        footer={<p className="mt-3 text-[12px] text-muted-foreground" data-testid="bk-order-payment">
                            Payment: <span className="font-semibold text-foreground">{PAY_LABEL[o.payment_method] || o.payment_method}</span> · {paid ? "paid" : String(o.payment_status || "").toLowerCase()}
                        </p>}
                    />

                    {track?.customer?.address_line && (
                        <BkCard testid="track-address">
                            <div className="flex items-start gap-3">
                                <MapPin size={18} className="text-primary shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                    <p className="text-[12px] font-semibold text-muted-foreground">Delivering to</p>
                                    <p className="text-[13.5px] leading-snug">{track.customer.address_line}</p>
                                </div>
                            </div>
                        </BkCard>
                    )}

                    <BkCard title="Timeline" testid="track-timeline">
                        {events.length === 0 ? <p className="text-[13px] text-muted-foreground">No updates yet.</p> : (
                            <ol className="relative space-y-3 before:absolute before:left-[5px] before:top-1.5 before:bottom-1.5 before:w-px before:bg-border">
                                {events.map((ev, i) => (
                                    <li key={i} className="relative flex items-start gap-3 pl-5" data-testid={`tl-${ev.status}`}>
                                        <span className={`absolute left-0 top-1.5 w-[11px] h-[11px] rounded-full border-2 border-card ${i === events.length - 1 ? "bg-primary" : "bg-foreground/30"}`} />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[13px] font-semibold">{(ev.status || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (ch) => ch.toUpperCase())}</p>
                                            {ev.message && <p className="text-[12px] text-muted-foreground">{ev.message}</p>}
                                        </div>
                                        <span className="text-[11px] text-muted-foreground shrink-0">{fmtTime(ev.ts)}</span>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </BkCard>

                    <BkCard title="Need help with this order?" icon={Headphones} testid="track-support-card">
                        <div className="flex flex-wrap gap-2">
                            {track?.support_contact?.whatsapp && (
                                <a href={track.support_contact.whatsapp.deep_link} target="_blank" rel="noreferrer" data-testid="track-support-whatsapp"
                                   className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-[12.5px] font-bold inline-flex items-center gap-1.5"><MessageCircle size={14} /> WhatsApp</a>
                            )}
                            {track?.support_contact?.call && (
                                <a href={track.support_contact.call.deep_link} data-testid="track-support-call"
                                   className="h-9 px-3 rounded-lg bg-foreground text-background text-[12.5px] font-bold inline-flex items-center gap-1.5"><Phone size={14} /> Call support</a>
                            )}
                            <Link to="/support" className="h-9 px-3 rounded-lg border border-border text-[12.5px] font-bold inline-flex items-center gap-1">Help centre <ChevronRight size={14} /></Link>
                        </div>
                    </BkCard>
                </div>
            </div>

            {hasMap && <div className="mt-3 lg:mt-5"><OrderTrackMap track={track} points={points} hasMap={hasMap} /></div>}

            {showReview && (
                <ReviewModal orderId={id} hasRider={!!o.rider_id} onClose={() => setShowReview(false)}
                             onSubmitted={() => { setShowReview(false); loadReviewState(); }} />
            )}
            {showReturnModal && (
                <div className="fixed inset-0 z-50 bg-black/45 flex items-end sm:items-center justify-center" onClick={() => !submittingReturn && setShowReturnModal(false)}>
                    <div className="bg-card w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}
                         style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }} data-testid="return-request-modal" role="dialog" aria-modal="true" aria-labelledby="bk-return-title">
                        <h2 id="bk-return-title" className="text-[17px] font-extrabold">Request a return</h2>
                        <p className="text-[12.5px] text-muted-foreground mt-1 mb-4">Tell us what went wrong. If approved, the refund goes to your wallet.</p>
                        <label htmlFor="bk-return-reason" className="text-[12px] font-bold text-muted-foreground">What happened?</label>
                        <textarea id="bk-return-reason" rows={3} value={returnReason} onChange={(e) => setReturnReason(e.target.value)} placeholder="E.g. the item arrived damaged"
                                  className="mt-1 w-full rounded-xl border border-border bg-muted/40 px-3 py-2.5 text-[14px] outline-none focus:border-primary focus:bg-card resize-none" data-testid="return-request-reason" />
                        <div className="flex gap-2 mt-4">
                            <button type="button" onClick={() => setShowReturnModal(false)} disabled={submittingReturn} data-testid="return-request-cancel"
                                    className="flex-1 h-11 rounded-xl bg-muted text-[13.5px] font-bold">Cancel</button>
                            <button type="button" onClick={submitReturnRequest} disabled={submittingReturn} data-testid="return-request-submit"
                                    className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground text-[13.5px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                                {submittingReturn && <Loader2 size={14} className="animate-spin" />} Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
